// src/components/AdviceContentBox.js

import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import adviceImage from '../assets/images/advice.png';
import { text as textColors } from '../utils/darkModeAdapter';

/**
 * [AdviceContentBox]
 *   - AIのアドバイスをChatGPT風に表示するコンポーネント
 *   - AIアバター + 吹き出しデザイン
 *   - advice（アドバイス）、reason（理由）、improvement（改善策）を分けて表示
 */
export default function AdviceContentBox({ adviceObj }) {
  const theme = useTheme();
  // 改善策があれば表示用に取り出す
  const hasImprovement = adviceObj && adviceObj.improvement;
  const improvementText = hasImprovement ? adviceObj.improvement.trim() : '';

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        display: 'flex',
        gap: 2,
        p: 3,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha('#2196F3', 0.05)}, ${alpha('#42A5F5', 0.03)})`,
        border: `1px solid ${alpha('#2196F3', 0.15)}`,
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        boxShadow: `0 4px 20px ${alpha('#2196F3', 0.1)}`,
      }}
    >
      {/* 背景装飾 */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha('#2196F3', 0.1)}, transparent)`,
          filter: 'blur(20px)',
        }}
      />

      {/* AIアバター画像 */}
      <Box
        sx={{
          flexShrink: 0,
          width: 200,
          height: 200,
          position: 'relative',
        }}
      >
        <img
          src={adviceImage}
          alt="AI人事アシスタント"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />

        {/* キラキラエフェクト */}
        <Box
          component={motion.div}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 24, color: '#FFD700' }} />
        </Box>
      </Box>

      {/* メッセージコンテンツ */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#249BE0',
            }}
          >
            AI人事アシスタント
          </Typography>
        </Box>

        {/* メインアドバイステキスト */}
        {adviceObj?.advice && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                color: textColors(theme).primary,
                lineHeight: 1.8,
                fontSize: '0.95rem',
              }}
            >
              {adviceObj.advice.trim()}
            </Typography>
          </Box>
        )}

        {/* 理由（reason） */}
        {adviceObj?.reason && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha('#FF9800', 0.08),
              border: `1px solid ${alpha('#FF9800', 0.2)}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: '#F57C00',
                fontWeight: 700,
                fontSize: '0.85rem',
                mb: 0.5,
              }}
            >
              理由
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                color: textColors(theme).primary,
                lineHeight: 1.7,
                fontSize: '0.9rem',
              }}
            >
              {adviceObj.reason.trim()}
            </Typography>
          </Box>
        )}

        {/* 改善策がある場合のみ、ハイライト表示 */}
        {hasImprovement && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha('#2196F3', 0.08),
              border: `1px dashed ${alpha('#2196F3', 0.25)}`,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AutoAwesomeIcon sx={{ fontSize: 18, color: '#2196F3' }} />
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#2196F3',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                }}
              >
                改善ポイント
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: textColors(theme).primary,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.7,
                fontSize: '0.9rem',
              }}
            >
              {improvementText}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}